import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  AcademicCapIcon,
  ArrowRightIcon,
  CalendarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  PencilIcon,
  RocketLaunchIcon,
  SparklesIcon,
  StarIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import {
  dashboardDataService,
  normalizeEntityId,
  resolveSelectionId,
  slotToDate,
  TeacherSelectionApi,
  toDateKey,
} from '../../services/dashboard-data.service';
import { examApi } from '../../services/exam.service';
import StudentSearch from '../../components/students/StudentSearch';
import DemoClassPanel from '../../components/education/DemoClassPanel';
import TeacherProfileForm from '../../components/teacher/TeacherProfileForm';
import { Session as ScheduledSession } from '../../types/session';
import { TeacherCertificationStatus } from '../../types/exam';

interface Student {
  id: string;
  name: string;
  subject: string;
  subjectId?: string;
  lastSession: string;
  progress: number;
  avatar: string;
}

interface TeacherProfileData {
  avatar?: string;
  status?: 'PENDING' | 'APPROVED' | 'REJECTED';
  location?: string;
  experience?: string;
  subjects?: string[];
  bio?: string;
  availabilitySlots?: string[];
  upiId?: string;
  upiNumber?: string;
  teachingCourseCertificate?: string;
  aadhaarMasked?: string;
  aadhaarKycStatus?: 'NOT_STARTED' | 'OTP_SENT' | 'VERIFIED' | 'FAILED';
  aadhaarKycUpdatedAt?: string;
  aadhaarKycVerifiedAt?: string;
  qualifications?: Array<{
    level: 'SCHOOL' | 'INTERMEDIATE' | 'DIPLOMA' | 'UG' | 'PG' | 'DOCTORATE' | 'CERTIFICATION' | 'OTHER';
    course: string;
    specialization?: string;
    institute?: string;
    yearOfCompletion?: string;
  }>;
  profileCompletionPercentage?: number;
  profileComplete?: boolean;
  certificationPassed?: boolean;
  leadsEnabled?: boolean;
}

type TeacherDashboardSession = ScheduledSession & {
  selectionId: string;
  sessionDate: string;
  subjectId?: string;
  meetingLink?: string;
  meetingCode?: string;
  offlineSessionType?: 'HOME_SINGLE' | 'GROUP';
};

const resolveReferralLink = (referralCode: string, rawReferralLink?: string) => {
  const normalizedCode = String(referralCode || '').trim();
  const fallbackLink = `${window.location.origin}/register?ref=${encodeURIComponent(normalizedCode)}`;
  const candidate = String(rawReferralLink || '').trim();

  if (!candidate) return fallbackLink;

  try {
    const parsed = new URL(candidate);
    const fallback = new URL(fallbackLink);
    parsed.protocol = fallback.protocol;
    parsed.host = fallback.host;
    return parsed.toString();
  } catch {
    return fallbackLink;
  }
};

const isGroupTuitionSession = (session: TeacherDashboardSession) =>
  session.classMode === 'OFFLINE' && session.offlineSessionType === 'GROUP';

const buildGroupSessionKey = (session: TeacherDashboardSession) =>
  [
    session.teacherId,
    session.sessionDate,
    new Date(session.scheduledAt).toISOString(),
    String(session.subjectId || session.subject || '').trim(),
    session.classMode,
    session.offlineSessionType || '',
  ].join('::');

const formatStudentCount = (count: number) => `${count} student${count === 1 ? '' : 's'}`;

const summarizeStudents = (sessions: TeacherDashboardSession[]) => {
  const names = sessions
    .map((session) => String(session.studentName || '').trim())
    .filter(Boolean);

  if (names.length === 0) return 'Student';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]} +${names.length - 2} more`;
};

const TeacherDashboard: React.FC = () => {
  const { user } = useAuth();
  const [referralCode, setReferralCode] = useState<string>(String((user as any)?.referralCode || '').trim());
  const [referralLink, setReferralLink] = useState<string>('');
  const [referralLoading, setReferralLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'sessions' | 'students' | 'analytics' | 'profile'>('sessions');
  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    hasActiveSubscription: boolean;
    planName?: string;
    expiresAt?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [teacherProfile, setTeacherProfile] = useState<TeacherProfileData | null>(null);
  const [certificationStatus, setCertificationStatus] = useState<TeacherCertificationStatus | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileFetchError, setProfileFetchError] = useState<string | null>(null);
  const [subjectLabelMap, setSubjectLabelMap] = useState<Record<string, string>>({});
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  const [profileEditRequested, setProfileEditRequested] = useState(false);
  const [teacherSelections, setTeacherSelections] = useState<TeacherSelectionApi[]>([]);
  const [qrSessions, setQrSessions] = useState<TeacherDashboardSession[]>([]);
  const [qrUrl, setQrUrl] = useState('');
  const [qrText, setQrText] = useState('');
  const [sendingSmsSessionId, setSendingSmsSessionId] = useState<string | null>(null);
  const [groupStartSession, setGroupStartSession] = useState<TeacherDashboardSession | null>(null);
  const [selectedGroupSessionIds, setSelectedGroupSessionIds] = useState<string[]>([]);
  const [rescheduleSessionId, setRescheduleSessionId] = useState<string | null>(null);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduleDateTime, setRescheduleDateTime] = useState('');
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);
  const profileSectionRef = useRef<HTMLDivElement | null>(null);

  const searchParams = new URLSearchParams(location.search);
  const shouldHighlightCertification = searchParams.get('showCertification') === '1';

  const ensureSubscriptionBeforeAction = useCallback(() => {
    // Dashboard should remain accessible even when subscription is inactive.
    // Subscription status is shown in UI and managed explicitly via plans page.
    return true;
  }, []);

  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (!ensureSubscriptionBeforeAction()) return;
    if (tab === 'sessions' || tab === 'students' || tab === 'analytics' || tab === 'profile') {
      setActiveTab(tab);
    }
  }, [location.search, ensureSubscriptionBeforeAction]);

  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      try {
        const data = await dashboardDataService.getTeacherSubscriptionStatus();
        setSubscriptionStatus(data);
      } catch (error) {
        console.error('Error fetching subscription status:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionStatus();
  }, []);

  useEffect(() => {
    const loadReferralSummary = async () => {
      try {
        const data = await dashboardDataService.getReferralSummary();
        const nextCode = String(data?.referralCode || '').trim();
        const nextLink = resolveReferralLink(nextCode, String(data?.referralLink || '').trim());
        setReferralCode(nextCode);
        setReferralLink(nextLink);
      } catch (error) {
        console.error('Failed to load referral summary:', error);
      }
    };

    void loadReferralSummary();
  }, []);

  useEffect(() => {
    const loadSubjectLabels = async () => {
      try {
        const nextMap = await dashboardDataService.getSubjectLabelMap();
        setSubjectLabelMap(nextMap);
      } catch (error) {
        console.error('Failed to load subject labels:', error);
        setSubjectLabelMap({});
      }
    };
    void loadSubjectLabels();
  }, []);

  const resolveSubjectLabel = useCallback(
    (subject?: string, subjectId?: string) => {
      const normalizedSubjectId = String(subjectId || '').trim();
      const normalizedSubject = String(subject || '').trim();
      return (
        subjectLabelMap[normalizedSubjectId] ||
        subjectLabelMap[normalizedSubject] ||
        normalizedSubject ||
        'General'
      );
    },
    [subjectLabelMap],
  );

  const loadTeacherSelections = useCallback(async (force = false) => {
    setIsLoading(true);
    try {
      const selections = await dashboardDataService.getTeacherSelections(force);
      setTeacherSelections(Array.isArray(selections) ? selections : []);
    } catch (error) {
      console.error('Error loading teacher selections:', error);
      setTeacherSelections([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTeacherSelections();
  }, [loadTeacherSelections]);

  const { totalStudentsCount, activeSessionsCount, averageRating, recentStudents, upcomingSessions, completedSessions } =
    useMemo(() => {
      const toRelativeTime = (value?: string) => {
        if (!value) return 'Recently';
        const now = Date.now();
        const selected = new Date(value).getTime();
        const diffHours = Math.max(1, Math.floor((now - selected) / (1000 * 60 * 60)));
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
        const diffMonths = Math.floor(diffDays / 30);
        return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
      };

      const canStillHaveUpcoming = (selection: TeacherSelectionApi) =>
        selection.frequency === 'DAILY' || selection.sessionStatus !== 'COMPLETED';

      const activeSelections = teacherSelections.filter(canStillHaveUpcoming);
      const uniqueStudents = new Set(
        activeSelections.map((selection) => normalizeEntityId(selection.studentUserId)).filter(Boolean),
      );

      const seenStudents = new Set<string>();
      const mappedRecentStudents: Student[] = [];
      const ratings: number[] = [];
      const now = new Date();
      const todayKey = toDateKey(now);
      const mappedUpcoming: TeacherDashboardSession[] = [];
      const mappedCompleted: TeacherDashboardSession[] = [];

      teacherSelections.forEach((selection) => {
        const studentId = normalizeEntityId(selection.studentUserId);
        if (studentId && !seenStudents.has(studentId)) {
          seenStudents.add(studentId);
          const fullName = (selection.studentName || '').trim() || 'Student';
          mappedRecentStudents.push({
            id: studentId,
            name: fullName,
            subject: selection.subject || 'General',
            subjectId: selection.subjectId,
            lastSession: toRelativeTime(selection.selectedAt),
            progress: Math.min(100, Math.max(50, Math.round(Number(selection.studentRating || 0) * 20) || 70)),
            avatar:
              fullName
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map((part) => part[0]?.toUpperCase() || '')
                .join('') || 'ST',
          });
        }

        if (typeof selection.studentRating === 'number' && Number.isFinite(selection.studentRating)) {
          ratings.push(selection.studentRating);
        }

        const selectionId = resolveSelectionId(selection);
        if (!selectionId) return;

        const duration = selection.classMode === 'OFFLINE' ? 90 : selection.classMode === 'HYBRID' ? 75 : 60;
        const baseSession = {
          teacherId: selection.teacherId,
          teacherName: selection.teacherName,
          studentId,
          studentName: selection.studentName || studentId || 'Student',
          subject: selection.subject,
          subjectId: selection.subjectId,
          meetingLink: selection.meetingLink,
          meetingCode: selection.meetingCode,
          classMode: selection.classMode,
          offlineSessionType: selection.offlineSessionType,
          frequency: selection.frequency,
          duration,
          selectionId,
        };

        if (selection.frequency === 'DAILY') {
          const statusMap = selection.sessionDailyStatus || {};
          const rescheduledDateTimes = selection.rescheduledDateTimes || {};
          const selectionStart = selection.startDate ? new Date(selection.startDate) : null;
          const selectionEnd = selection.endDate ? new Date(selection.endDate) : null;
          if (selectionStart && !Number.isNaN(selectionStart.getTime())) {
            selectionStart.setHours(0, 0, 0, 0);
          }
          if (selectionEnd && !Number.isNaN(selectionEnd.getTime())) {
            selectionEnd.setHours(23, 59, 59, 999);
          }
          for (let offset = -7; offset <= 6; offset += 1) {
            const day = new Date(now);
            day.setHours(0, 0, 0, 0);
            day.setDate(day.getDate() + offset);
            if (selectionStart && day.getTime() < selectionStart.getTime()) continue;
            if (selectionEnd && day.getTime() > selectionEnd.getTime()) continue;
            const dayKey = toDateKey(day);
            const status = statusMap[dayKey] === 'COMPLETED' ? 'COMPLETED' : 'SCHEDULED';
            const row: TeacherDashboardSession = {
              id: `${selectionId}:${dayKey}`,
              ...baseSession,
              sessionDate: dayKey,
              scheduledAt:
                rescheduledDateTimes[dayKey] || slotToDate(day.toISOString(), selection.preferredTimeSlot),
              status,
            };

            if (offset < 0 || (offset === 0 && status === 'COMPLETED')) {
              mappedCompleted.push(row);
            } else {
              mappedUpcoming.push(row);
            }
          }
          return;
        }

        const scheduledAt = slotToDate(selection.selectedAt, selection.preferredTimeSlot);
        const baseDayKey = toDateKey(new Date(scheduledAt));
        const scheduledAtResolved = (selection.rescheduledDateTimes || {})[baseDayKey] || scheduledAt;
        const status = selection.sessionStatus === 'COMPLETED' ? 'COMPLETED' : 'SCHEDULED';
        const row: TeacherDashboardSession = {
          id: selectionId,
          ...baseSession,
          sessionDate: toDateKey(new Date(scheduledAtResolved)),
          scheduledAt: scheduledAtResolved,
          status,
        };
        if (status === 'COMPLETED' || new Date(scheduledAtResolved).getTime() < Date.now()) {
          mappedCompleted.push(row);
        } else {
          mappedUpcoming.push(row);
        }
      });

      mappedUpcoming.sort((left, right) => new Date(left.scheduledAt).getTime() - new Date(right.scheduledAt).getTime());
      mappedCompleted.sort((left, right) => new Date(right.scheduledAt).getTime() - new Date(left.scheduledAt).getTime());

      return {
        totalStudentsCount: uniqueStudents.size,
        activeSessionsCount: mappedUpcoming.filter((session) => session.sessionDate === todayKey).length,
        averageRating: ratings.length > 0
          ? (ratings.reduce((sum, value) => sum + value, 0) / ratings.length).toFixed(1)
          : '0.0',
        recentStudents: mappedRecentStudents,
        upcomingSessions: mappedUpcoming,
        completedSessions: mappedCompleted,
      };
    }, [teacherSelections]);

  const groupSessionsById = useMemo(() => {
    const groupedSessions = new Map<string, TeacherDashboardSession[]>();

    upcomingSessions.forEach((session) => {
      if (!isGroupTuitionSession(session)) return;
      const key = buildGroupSessionKey(session);
      const existing = groupedSessions.get(key) || [];
      existing.push(session);
      groupedSessions.set(key, existing);
    });

    const sessionsById = new Map<string, TeacherDashboardSession[]>();
    groupedSessions.forEach((sessions) => {
      const sortedSessions = [...sessions].sort((left, right) => left.studentName.localeCompare(right.studentName));
      sortedSessions.forEach((session) => {
        sessionsById.set(session.id, sortedSessions);
      });
    });

    return sessionsById;
  }, [upcomingSessions]);

  const resolveGroupedSessions = (session: TeacherDashboardSession) => groupSessionsById.get(session.id) || [session];
  const groupStartOptions = groupStartSession ? resolveGroupedSessions(groupStartSession) : [];
  const qrPrimarySession = qrSessions[0] || null;

  const stats = useMemo(
    () => [
      { name: 'Total Students', value: String(totalStudentsCount), change: '', changeType: 'neutral', icon: UserGroupIcon },
      { name: 'Active Sessions', value: String(activeSessionsCount), change: '', changeType: 'neutral', icon: CalendarIcon },
      { name: 'Monthly Earnings', value: 'INR 2,450', change: '+INR 120', changeType: 'positive', icon: CurrencyDollarIcon },
      { name: 'Rating', value: averageRating, change: '', changeType: 'neutral', icon: StarIcon },
    ] as const,
    [activeSessionsCount, averageRating, totalStudentsCount],
  );

  const generateMeetingCode = () => Math.random().toString(36).slice(2, 12).toUpperCase();

  const handleSendToMobile = async (sessionId: string) => {
    const session = upcomingSessions.find((s) => s.id === sessionId);
    if (!session) return;
    
    setSendingSmsSessionId(sessionId);
    try {
      const response = await api.post('/teachers/selections/send-meeting-details', {
        selectionId: session.selectionId,
      });
      
      if (response.data.success) {
        alert('Meeting details sent to student\'s mobile number successfully!');
      } else {
        alert(response.data.message || 'Failed to send meeting details');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 'Failed to send meeting details. Please try again.';
      alert(errorMessage);
    } finally {
      setSendingSmsSessionId((current) => (current === sessionId ? null : current));
    }
  };

  const closeGroupStartModal = () => {
    setGroupStartSession(null);
    setSelectedGroupSessionIds([]);
  };

  const toggleGroupStartSession = (sessionId: string) => {
    setSelectedGroupSessionIds((current) =>
      current.includes(sessionId)
        ? current.filter((selectedSessionId) => selectedSessionId !== sessionId)
        : [...current, sessionId],
    );
  };

  const startSessions = async (sessionsToStart: TeacherDashboardSession[]) => {
    if (!sessionsToStart.length) return false;

    const targetSessions = [...sessionsToStart].sort((left, right) => left.studentName.localeCompare(right.studentName));
    const primarySession = targetSessions[0];
    let meetingLink = '';
    let meetingCode = '';
    let startedSessions: TeacherDashboardSession[] = [];
    let syncFailedSessions: TeacherDashboardSession[] = [];

    if (primarySession.classMode === 'ONLINE') {
      try {
        const response = await api.post(`/teachers/selections/${primarySession.selectionId}/google-meet`);
        meetingLink = String(response.data?.meetingLink || '').trim();
        meetingCode = String(response.data?.meetingCode || '').trim();
        if (!meetingLink) {
          throw new Error('Google Meet link was not returned');
        }
      } catch (error: any) {
        const message = error?.response?.data?.message || error?.message || 'Failed to create Google Meet link.';
        alert(message);
        return false;
      }

      const syncTargets = targetSessions.filter(
        (targetSession) => targetSession.selectionId !== primarySession.selectionId,
      );
      const syncResults = await Promise.allSettled(
        syncTargets.map((targetSession) =>
          api.patch(`/teachers/selections/${targetSession.selectionId}/meeting`, {
            meetingLink,
            meetingCode,
          }),
        ),
      );

      const syncedSessions = syncTargets.filter((_, index) => syncResults[index]?.status === 'fulfilled');
      syncFailedSessions = syncTargets.filter((_, index) => syncResults[index]?.status === 'rejected');
      startedSessions = [primarySession, ...syncedSessions];
      window.open(meetingLink, '_blank', 'noopener,noreferrer');
    } else {
      meetingCode = generateMeetingCode();
      meetingLink = `https://quickchart.io/qr?size=320&text=${encodeURIComponent(meetingCode)}`;

      const saveResults = await Promise.allSettled(
        targetSessions.map((targetSession) =>
          api.patch(`/teachers/selections/${targetSession.selectionId}/meeting`, {
            meetingLink,
            meetingCode,
          }),
        ),
      );

      startedSessions = targetSessions.filter((_, index) => saveResults[index]?.status === 'fulfilled');
      syncFailedSessions = targetSessions.filter((_, index) => saveResults[index]?.status === 'rejected');

      if (!startedSessions.length) {
        alert('Failed to save meeting details for this session.');
        return false;
      }

      setQrUrl(meetingLink);
      setQrText(meetingCode);
      setQrSessions(startedSessions);
    }

    const smsResults = await Promise.allSettled(
      startedSessions.map((startedSession) =>
        api.post('/teachers/selections/send-meeting-details', {
          selectionId: startedSession.selectionId,
        }),
      ),
    );
    const smsFailedSessions = startedSessions.filter((_, index) => smsResults[index]?.status === 'rejected');

    if (syncFailedSessions.length || smsFailedSessions.length) {
      const warnings: string[] = [];
      if (syncFailedSessions.length) {
        warnings.push(`Meeting details were not saved for ${formatStudentCount(syncFailedSessions.length)}.`);
      }
      if (smsFailedSessions.length) {
        warnings.push(`Delivery failed for ${formatStudentCount(smsFailedSessions.length)}.`);
      }
      alert(`Session started. ${warnings.join(' ')}`);
    }

    dashboardDataService.invalidateTeacherSelections();
    void loadTeacherSelections(true);
    return true;
  };

  const handleStartSession = async (sessionId: string) => {
    const session = upcomingSessions.find((s) => s.id === sessionId);
    if (!session) return;

    const groupedSessions = resolveGroupedSessions(session);
    if (groupedSessions.length > 1) {
      setGroupStartSession(session);
      setSelectedGroupSessionIds(groupedSessions.map((groupSession) => groupSession.id));
      return;
    }

    await startSessions([session]);
  };

  const startSelectedGroupSessions = async () => {
    const selectedSessions = groupStartOptions.filter((session) => selectedGroupSessionIds.includes(session.id));
    if (!selectedSessions.length) {
      alert('Select at least one student to start this group session.');
      return;
    }

    const started = await startSessions(selectedSessions);
    if (started) {
      closeGroupStartModal();
    }
  };

  const handleViewStudent = (studentId: string) => {
    const student = recentStudents.find((entry) => entry.id === studentId);
    setStudentSearchQuery(student?.name || '');
    setActiveTab('students');
    navigate('/teacher/dashboard?tab=students');
  };

  const fetchTeacherProfile = async () => {
    try {
      setProfileLoading(true);
      setProfileFetchError(null);
      const response = await api.get<TeacherProfileData>('/teachers/profile');
      const avatarLength = String((response.data as any)?.avatar || '').length;
      console.log('[Avatar Debug] /teachers/profile avatar length:', avatarLength);
      setTeacherProfile(response.data || null);
    } catch (error) {
      console.error('Error loading teacher profile:', error);
      const status = (error as any)?.response?.status;
      setProfileFetchError(
        status ? `profile_fetch_failed_${status}` : 'profile_fetch_failed',
      );
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchTeacherReadinessStatus = useCallback(async () => {
    try {
      const [profileRes, certificationRes] = await Promise.allSettled([
        api.get<TeacherProfileData>('/teachers/profile'),
        examApi.getTeacherCertificationStatus(),
      ]);

      if (profileRes.status === 'fulfilled') {
        const avatarLength = String((profileRes.value.data as any)?.avatar || '').length;
        console.log('[Avatar Debug] readiness /teachers/profile avatar length:', avatarLength);
        setTeacherProfile(profileRes.value.data || null);
      }
      if (certificationRes.status === 'fulfilled') {
        setCertificationStatus(certificationRes.value || null);
      }
    } catch (error) {
      console.error('Error loading teacher readiness status:', error);
    }
  }, []);

  useEffect(() => {
    void fetchTeacherReadinessStatus();
  }, [fetchTeacherReadinessStatus]);

  useEffect(() => {
    if (activeTab !== 'profile') return;
    if (teacherProfile || profileLoading || profileFetchError) return;
    void fetchTeacherProfile();
  }, [activeTab, teacherProfile, profileLoading, profileFetchError]);

  useEffect(() => {
    if (activeTab !== 'profile') return;
    const timer = window.setTimeout(() => {
      profileSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);

    return () => window.clearTimeout(timer);
  }, [activeTab]);

  const handleOpenProfile = async () => {
    if (!ensureSubscriptionBeforeAction()) return;
    setProfileEditRequested(true);
    setActiveTab('profile');
    setProfileFetchError(null);
    await fetchTeacherProfile();
  };

  const handleTabChange = (tab: 'sessions' | 'students' | 'analytics') => {
    if (!ensureSubscriptionBeforeAction()) return;
    setProfileEditRequested(false);
    setActiveTab(tab);
  };

  const handleReferFriend = async () => {
    try {
      setReferralLoading(true);
      const response = await api.post('/users/me/referral/generate');
      dashboardDataService.invalidateReferralSummary();
      const nextCode = String(response.data?.referralCode || '').trim();
      const nextLink = resolveReferralLink(nextCode, String(response.data?.referralLink || '').trim());
      if (!nextCode) {
        alert('Referral code could not be generated.');
        return;
      }
      setReferralCode(nextCode);
      setReferralLink(nextLink);
      const shareValue = nextLink;
      const sharePayload = {
        title: 'Join LocalAlly',
        text: `Use my referral link to sign up on LocalAlly.`,
        url: shareValue,
      };

      if (navigator?.share) {
        await navigator.share(sharePayload);
      } else if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareValue);
        alert(`Referral link copied: ${shareValue}`);
      } else {
        alert(`Referral link: ${shareValue}`);
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to generate referral code.';
      alert(message);
    } finally {
      setReferralLoading(false);
    }
  };

  const readinessBanner = React.useMemo(() => {
    if (!teacherProfile) return null;

    const approvalStatus = String(teacherProfile.status || 'PENDING').toUpperCase();
    const profileComplete = Boolean(
      teacherProfile.profileComplete ||
      Number(teacherProfile.profileCompletionPercentage || 0) >= 100,
    );
    const certificationKnown = Boolean(certificationStatus);
    const hasCertificationAttempt = Boolean(certificationStatus?.latestAttempt);
    const certificationPassed = Boolean(
      teacherProfile.certificationPassed ?? certificationStatus?.isPassed,
    );

    if (approvalStatus === 'APPROVED' && profileComplete && certificationPassed) {
      return {
        tone: 'success' as const,
        title: 'You are eligible to earn now',
        message:
          'Great job. Your profile is complete, certification is passed, and admin approval is done. You can now receive leads and start earning.',
        cta: null as null | { label: string; onClick: () => void },
      };
    }

    if (approvalStatus === 'REJECTED') {
      return {
        tone: 'danger' as const,
        title: 'Admin review requires updates',
        message:
          'Your profile was rejected by admin. Update profile details and certification readiness, then request review again.',
        cta: { label: 'Update Profile', onClick: () => setActiveTab('profile') },
      };
    }

    if (!profileComplete) {
      return {
        tone: 'warning' as const,
        title: 'Profile is incomplete',
        message:
          'Complete your profile (subjects, availability, bio, qualification, and UPI details) to move to the next step.',
        cta: { label: 'Complete Profile', onClick: () => setActiveTab('profile') },
      };
    }

    if (certificationKnown && !hasCertificationAttempt) {
      return {
        tone: 'info' as const,
        title: 'Certification exam not attempted',
        message:
          'Attempt and pass the teacher certification exam to become eligible for admin approval and student leads.',
        cta: { label: 'Attempt Exam', onClick: () => navigate('/teacher/certification-exam') },
      };
    }

    if (certificationKnown && !certificationPassed) {
      return {
        tone: 'warning' as const,
        title: 'Certification exam not passed yet',
        message: 'Please retry the certification exam and pass it to proceed for admin approval.',
        cta: { label: 'Retry Exam', onClick: () => navigate('/teacher/certification-exam') },
      };
    }

    if (approvalStatus === 'PENDING') {
      return {
        tone: 'info' as const,
        title: 'Waiting for admin approval',
        message:
          'Your profile and certification are complete. Admin verification is pending. You will be eligible to earn once approved.',
        cta: null as null | { label: string; onClick: () => void },
      };
    }

    return null;
  }, [teacherProfile, certificationStatus, navigate]);

  const bannerToneClasses: Record<'success' | 'warning' | 'info' | 'danger', string> = {
    success: 'border-green-200 bg-green-50 text-green-800',
    warning: 'border-amber-200 bg-amber-50 text-amber-800',
    info: 'border-blue-200 bg-blue-50 text-blue-800',
    danger: 'border-red-200 bg-red-50 text-red-800',
  };

  const formatSessionTime = (scheduledAt: string) =>
    new Date(scheduledAt).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const toDateTimeLocal = (value: Date) => {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    const hours = String(value.getHours()).padStart(2, '0');
    const minutes = String(value.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const handleRescheduleRequest = (sessionId: string) => {
    const current = upcomingSessions.find((session) => session.id === sessionId);
    if (!current) return;

    const defaultDate = new Date(current.scheduledAt);
    defaultDate.setDate(defaultDate.getDate() + 1);
    setRescheduleSessionId(sessionId);
    setRescheduleReason('');
    setRescheduleDateTime(toDateTimeLocal(defaultDate));
    setRescheduleError(null);
  };

  const closeRescheduleModal = () => {
    setRescheduleSessionId(null);
    setRescheduleReason('');
    setRescheduleDateTime('');
    setRescheduleError(null);
  };

  const submitRescheduleRequest = async () => {
    if (!rescheduleSessionId) return;
    const reason = rescheduleReason.trim();
    if (!reason) {
      setRescheduleError('Please provide a reason for rescheduling.');
      return;
    }
    if (!rescheduleDateTime) {
      setRescheduleError('Please choose proposed date and time.');
      return;
    }
    const parsedProposed = new Date(rescheduleDateTime);
    if (Number.isNaN(parsedProposed.getTime())) {
      setRescheduleError('Invalid proposed date/time.');
      return;
    }
    const proposedIso = parsedProposed.toISOString();
    const currentSession = upcomingSessions.find((session) => session.id === rescheduleSessionId);
    if (!currentSession) {
      setRescheduleError('Session not found.');
      return;
    }
    try {
      await api.post('/teachers/selections/reschedule-request', {
        selectionId: currentSession.selectionId,
        sessionDate: currentSession.sessionDate,
        reason,
        proposedScheduledAt: proposedIso,
      });
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to submit reschedule request.';
      setRescheduleError(typeof message === 'string' ? message : 'Failed to submit reschedule request.');
      return;
    }

    alert('Reschedule request sent to admin for approval.');
    dashboardDataService.invalidateTeacherSelections();
    void loadTeacherSelections(true);
    closeRescheduleModal();
  };

  const handleSetStatus = async (selectionId: string, sessionDate: string, status: 'SCHEDULED' | 'COMPLETED') => {
    try {
      await api.patch(`/teachers/selections/${selectionId}/status`, {
        sessionDate,
        status,
      });
      dashboardDataService.invalidateTeacherSelections();
      await loadTeacherSelections(true);
    } catch (error: any) {
      const message =
        error?.response?.data?.message?.message ||
        error?.response?.data?.message ||
        'Failed to update session status.';
      alert(message);
    }
  };

  const teacherDisplayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() ||
    user?.lastName ||
    user?.firstName ||
    'Teacher';

  const profileComplete = Boolean(
    teacherProfile?.profileComplete ||
    Number(teacherProfile?.profileCompletionPercentage || 0) >= 100,
  );
  const certificationPassed = Boolean(
    teacherProfile?.certificationPassed ?? certificationStatus?.isPassed,
  );
  const approvalStatus = String(teacherProfile?.status || 'PENDING').toUpperCase();
  const adminApproved = approvalStatus === 'APPROVED';
  const profileNeedsSetup = !profileComplete;
  const shouldStartProfileEditing = profileEditRequested || shouldHighlightCertification || profileNeedsSetup;

  const todayKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(
    new Date().getDate(),
  ).padStart(2, '0')}`;

  const tabButtonClasses = (tab: 'sessions' | 'students' | 'analytics' | 'profile') =>
    `inline-flex items-center rounded-full px-4 py-2 text-sm font-semibold transition ${
      activeTab === tab
        ? 'bg-slate-950 text-white shadow-lg shadow-slate-950/20'
        : 'bg-white/70 text-slate-600 ring-1 ring-slate-200 hover:bg-white hover:text-slate-900'
    }`;

  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white/80 px-6 py-16 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.45)] backdrop-blur">
        <div className="mx-auto flex max-w-sm flex-col items-center text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-950 text-white shadow-lg shadow-slate-950/20">
            <SparklesIcon className="h-8 w-8 animate-pulse" />
          </div>
          <p className="mt-5 text-lg font-semibold text-slate-900">Loading your teaching command center</p>
          <p className="mt-2 text-sm text-slate-500">Sessions, students, profile readiness, and payouts are being prepared.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-4">
      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(56,189,248,0.22),_transparent_28%),linear-gradient(135deg,_#020617_0%,_#0f172a_46%,_#1d4ed8_100%)] text-white shadow-[0_30px_90px_-48px_rgba(15,23,42,0.65)]">
        <div className="grid gap-6 px-5 py-6 sm:px-7 lg:grid-cols-[minmax(0,1.7fr)_minmax(320px,0.9fr)] lg:px-8 lg:py-8">
          <div className="space-y-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-sky-100">
              <SparklesIcon className="h-4 w-4" />
              Teacher Dashboard
            </div>
            <div className="max-w-3xl space-y-3">
              <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
                {teacherDisplayName}, your teacher workspace is ready.
              </h1>
              <p className="max-w-2xl text-sm leading-6 text-slate-200 sm:text-base">
                Complete your profile, pass certification, wait for admin approval, then manage leads, classes, and payouts here.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Today&apos;s classes</p>
                <p className="mt-3 text-3xl font-semibold text-white">{activeSessionsCount}</p>
                <p className="mt-2 text-xs text-slate-300">Scheduled for {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Student reach</p>
                <p className="mt-3 text-3xl font-semibold text-white">{totalStudentsCount}</p>
                <p className="mt-2 text-xs text-slate-300">Active learners across your current selections</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Teacher rating</p>
                <p className="mt-3 text-3xl font-semibold text-white">{averageRating}</p>
                <p className="mt-2 text-xs text-slate-300">Based on submitted student feedback</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleOpenProfile}
                className="inline-flex items-center justify-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
              >
                <PencilIcon className="mr-2 h-5 w-5" />
                Edit Profile
              </button>
              <Link
                to="/teacher/dashboard?tab=sessions"
                className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-5 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/15"
              >
                <CalendarIcon className="mr-2 h-5 w-5" />
                View Sessions
              </Link>
              <Link
                to="/teacher/payments"
                className="inline-flex items-center justify-center rounded-full border border-emerald-300/35 bg-emerald-400/10 px-5 py-3 text-sm font-semibold text-emerald-100 transition hover:bg-emerald-400/20"
              >
                <CurrencyDollarIcon className="mr-2 h-5 w-5" />
                Payments
              </Link>
              <button
                type="button"
                onClick={handleReferFriend}
                disabled={referralLoading}
                className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-5 py-3 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/15 disabled:opacity-60"
              >
                {referralLoading ? 'Generating...' : referralCode ? 'Copy Referral Link' : 'Refer Friend'}
              </button>
            </div>
            {referralCode && (
              <p className="text-xs text-slate-300">
                Referral code: {referralCode}
                {referralLink ? ' | Link ready to share' : ''}
              </p>
            )}
          </div>
          <div className="grid gap-4">
            <div className="rounded-[1.75rem] border border-white/12 bg-white/10 p-5 backdrop-blur">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Subscription</p>
                  <p className="mt-2 text-xl font-semibold text-white">
                    {loading
                      ? 'Checking status...'
                      : subscriptionStatus?.hasActiveSubscription
                        ? subscriptionStatus.planName || 'Active plan'
                        : 'No active plan'}
                  </p>
                  <p className="mt-2 text-sm text-slate-300">
                    {loading
                      ? 'Fetching current subscription details.'
                      : subscriptionStatus?.hasActiveSubscription
                        ? subscriptionStatus.expiresAt
                          ? `Renews on ${new Date(subscriptionStatus.expiresAt).toLocaleDateString()}`
                          : 'Your teacher plan is active.'
                        : 'Activate a plan to keep leads and premium tools uninterrupted.'}
                  </p>
                </div>
                <div className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                  subscriptionStatus?.hasActiveSubscription
                    ? 'bg-emerald-400/15 text-emerald-100'
                    : 'bg-amber-400/15 text-amber-100'
                }`}>
                  {subscriptionStatus?.hasActiveSubscription ? 'Active' : 'Attention'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate('/teacher/subscription-plans')}
                className="mt-5 inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                Manage plan
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </button>
            </div>
            <div className="rounded-[1.75rem] border border-white/12 bg-slate-950/20 p-5 backdrop-blur">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
                  <RocketLaunchIcon className="h-5 w-5 text-sky-100" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">Teacher setup path</p>
                  <p className="text-xs text-slate-300">Follow this order to become visible to students.</p>
                </div>
              </div>

              <div className="mt-4 space-y-2">
                <button
                  type="button"
                  onClick={handleOpenProfile}
                  className="flex w-full items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/10 px-3 py-3 text-left transition hover:bg-white/15"
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-slate-950">1</span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-white">Complete profile</span>
                      <span className="block text-xs text-slate-300">Subjects, location, bio, UPI, availability</span>
                    </span>
                  </span>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                    profileComplete ? 'bg-emerald-400/15 text-emerald-100' : 'bg-amber-400/15 text-amber-100'
                  }`}>
                    {profileComplete ? 'Done' : 'Start'}
                  </span>
                </button>

                <Link
                  to="/teacher/certification-exam"
                  className="flex w-full items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/10 px-3 py-3 text-left transition hover:bg-white/15"
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-slate-950">2</span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-white">Pass certification</span>
                      <span className="block text-xs text-slate-300">Required before admin approval</span>
                    </span>
                  </span>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                    certificationPassed
                      ? 'bg-emerald-400/15 text-emerald-100'
                      : profileComplete
                        ? 'bg-sky-400/15 text-sky-100'
                        : 'bg-white/10 text-slate-300'
                  }`}>
                    {certificationPassed ? 'Done' : profileComplete ? 'Next' : 'Later'}
                  </span>
                </Link>

                <div className="flex w-full items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/10 px-3 py-3">
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-slate-950">3</span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-white">Admin approval</span>
                      <span className="block text-xs text-slate-300">Review happens after profile and exam</span>
                    </span>
                  </span>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                    adminApproved
                      ? 'bg-emerald-400/15 text-emerald-100'
                      : profileComplete && certificationPassed
                        ? 'bg-sky-400/15 text-sky-100'
                        : 'bg-white/10 text-slate-300'
                  }`}>
                    {adminApproved ? 'Done' : profileComplete && certificationPassed ? 'Pending' : 'Later'}
                  </span>
                </div>

                <Link
                  to="/teacher/subscription-plans"
                  className="flex w-full items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/10 px-3 py-3 text-left transition hover:bg-white/15"
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white text-xs font-semibold text-slate-950">4</span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-white">Activate plan and get leads</span>
                      <span className="block text-xs text-slate-300">Use Leads after approval</span>
                    </span>
                  </span>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                    subscriptionStatus?.hasActiveSubscription
                      ? 'bg-emerald-400/15 text-emerald-100'
                      : adminApproved
                        ? 'bg-sky-400/15 text-sky-100'
                        : 'bg-white/10 text-slate-300'
                  }`}>
                    {subscriptionStatus?.hasActiveSubscription ? 'Active' : adminApproved ? 'Choose' : 'Later'}
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {readinessBanner && (
          <div className={`mx-5 mb-5 rounded-[1.5rem] border px-4 py-4 sm:mx-7 lg:mx-8 ${bannerToneClasses[readinessBanner.tone]}`}>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold">{readinessBanner.title}</p>
                <p className="mt-1 text-sm">{readinessBanner.message}</p>
              </div>
              {readinessBanner.cta && (
                <button
                  type="button"
                  onClick={readinessBanner.cta.onClick}
                  className="inline-flex items-center rounded-md bg-white/80 px-3 py-2 text-xs font-semibold hover:bg-white"
                >
                  {readinessBanner.cta.label}
                </button>
              )}
            </div>
          </div>
        )}
      </section>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.name}
              className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white/90 p-5 shadow-[0_20px_60px_-40px_rgba(15,23,42,0.35)]"
            >
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-950 text-white shadow-lg shadow-slate-950/10">
                  <Icon className="h-6 w-6" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{stat.name}</p>
                  <div className="mt-3 flex items-end gap-2">
                    <p className="text-3xl font-semibold tracking-tight text-slate-950">{stat.value}</p>
                    {stat.change ? (
                      <span className={`text-sm font-semibold ${
                        stat.changeType === 'positive' ? 'text-emerald-600' : 'text-rose-600'
                      }`}>
                        {stat.change}
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-2 text-sm text-slate-500">
                    {stat.name === 'Monthly Earnings'
                      ? 'Snapshot of this month&apos;s expected payout trend.'
                      : stat.name === 'Rating'
                        ? 'Average student sentiment across your completed sessions.'
                        : stat.name === 'Active Sessions'
                          ? 'Sessions lined up for today and currently active.'
                          : 'Students currently tied to your class selections.'}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="overflow-x-auto">
        <nav className="flex w-max gap-2 rounded-full border border-slate-200 bg-slate-100/80 p-1">
          <button onClick={() => handleTabChange('sessions')} className={tabButtonClasses('sessions')}>
            Sessions
          </button>
          <button onClick={() => handleTabChange('students')} className={tabButtonClasses('students')}>
            Students
          </button>
          <button onClick={() => handleTabChange('analytics')} className={tabButtonClasses('analytics')}>
            Analytics
          </button>
          <button onClick={handleOpenProfile} className={tabButtonClasses('profile')}>
            Profile
          </button>
        </nav>
      </div>

      {activeTab === 'profile' ? (
        <div
          ref={profileSectionRef}
          className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]"
        >
          <div className="border-b border-slate-200 bg-slate-950 px-5 py-5 text-white sm:px-6">
            <h3 className="text-xl font-semibold tracking-tight">My Teaching Profile</h3>
            <p className="mt-1 text-sm text-slate-300">Manage your public profile, teaching preferences, certificates, and availability.</p>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <TeacherProfileForm
              onUpdate={() => {
                setProfileEditRequested(false);
                handleTabChange('sessions');
              }}
              profileData={teacherProfile}
              profileLoading={profileLoading}
              showCertificationSection={shouldHighlightCertification}
              startInEditMode={shouldStartProfileEditing}
            />
          </div>
        </div>
      ) : activeTab === 'sessions' ? (
        <div className="space-y-6">
          <DemoClassPanel compact />

          <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
            <div className="border-b border-slate-200 bg-gradient-to-r from-slate-950 via-slate-900 to-sky-900 px-5 py-5 text-white sm:px-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h3 className="text-xl font-semibold tracking-tight">Upcoming Sessions</h3>
                  <p className="mt-1 text-sm text-slate-300">Run today&apos;s classes, share attendance access, and keep schedule changes organized.</p>
                </div>
                <div className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-200">
                  {upcomingSessions.length} queued
                </div>
              </div>
            </div>

            <div className="grid gap-4 border-b border-slate-200 bg-slate-50/80 px-5 py-4 sm:grid-cols-3 sm:px-6">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Today</p>
                <p className="mt-2 text-2xl font-semibold text-slate-950">{upcomingSessions.filter((session) => session.sessionDate === todayKey).length}</p>
                <p className="mt-1 text-sm text-slate-500">Sessions scheduled for today.</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Online ready</p>
                <p className="mt-2 text-2xl font-semibold text-slate-950">{upcomingSessions.filter((session) => session.classMode === 'ONLINE').length}</p>
                <p className="mt-1 text-sm text-slate-500">Classes that can launch with direct links.</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Offline QR</p>
                <p className="mt-2 text-2xl font-semibold text-slate-950">{upcomingSessions.filter((session) => session.classMode !== 'ONLINE').length}</p>
                <p className="mt-1 text-sm text-slate-500">Sessions using QR or meeting code verification.</p>
              </div>
            </div>

            <div className="md:hidden divide-y divide-slate-200">
              {upcomingSessions.length === 0 ? (
                <div className="px-5 py-8 text-sm text-slate-500">No upcoming sessions</div>
              ) : (
                upcomingSessions.map((session) => {
                  const groupedSessions = resolveGroupedSessions(session);
                  const isSelectableGroup = groupedSessions.length > 1;

                  return (
                    <div key={session.id} className="space-y-4 px-5 py-5">
                      <div className="flex items-start gap-3">
                        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-sm font-semibold text-white">
                          {session.studentName.split(' ').map((name) => name[0]).join('').slice(0, 2)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-base font-semibold text-slate-950">{session.studentName}</p>
                              <p className="mt-1 text-sm font-medium text-sky-700">{resolveSubjectLabel(session.subject, session.subjectId)}</p>
                            </div>
                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                              {session.duration} min
                            </span>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">{session.classMode}</span>
                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{session.frequency}</span>
                            {isSelectableGroup && (
                              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                                Group • {formatStudentCount(groupedSessions.length)}
                              </span>
                            )}
                          </div>
                          <p className="mt-3 text-sm text-slate-500">{formatSessionTime(session.scheduledAt)}</p>
                          {isSelectableGroup && (
                            <p className="mt-2 text-xs text-slate-500">
                              Start once and choose which students join this shared group slot.
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => handleStartSession(session.id)}
                          className="rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
                        >
                          {isSelectableGroup ? 'Start Group' : 'Start'}
                        </button>
                        <button
                          onClick={() => handleSendToMobile(session.id)}
                          disabled={sendingSmsSessionId === session.id}
                          className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm font-semibold text-sky-700 transition hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {sendingSmsSessionId === session.id ? 'Sending...' : 'Send'}
                        </button>
                        <button
                          onClick={() => handleRescheduleRequest(session.id)}
                          className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                        >
                          Reschedule
                        </button>
                        {session.sessionDate === todayKey ? (
                          <button
                            onClick={() => handleSetStatus(session.selectionId, session.sessionDate, 'COMPLETED')}
                            className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100"
                          >
                            Complete
                          </button>
                        ) : (
                          <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">
                            Upcoming
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Student
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Session
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Schedule
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {upcomingSessions.map((session) => {
                    const groupedSessions = resolveGroupedSessions(session);
                    const isSelectableGroup = groupedSessions.length > 1;

                    return (
                      <tr key={session.id} className="transition hover:bg-slate-50/80">
                        <td className="px-6 py-4 align-top">
                          <div className="flex items-center gap-3">
                            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-slate-950 text-sm font-semibold text-white">
                              {session.studentName.split(' ').map((name) => name[0]).join('').slice(0, 2)}
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-950">{session.studentName}</p>
                              <p className="text-xs text-slate-500">{session.duration} min lesson</p>
                              {isSelectableGroup && (
                                <p className="mt-1 text-xs text-emerald-700">
                                  Group slot with {formatStudentCount(groupedSessions.length)}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 align-top">
                          <p className="text-sm font-semibold text-sky-700">{resolveSubjectLabel(session.subject, session.subjectId)}</p>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">{session.classMode}</span>
                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">{session.frequency}</span>
                            {isSelectableGroup && (
                              <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                                Select multiple
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 align-top">
                          <p className="text-sm font-medium text-slate-900">{formatSessionTime(session.scheduledAt)}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            {session.sessionDate === todayKey ? 'Scheduled for today' : `Session date ${session.sessionDate}`}
                          </p>
                        </td>
                        <td className="px-6 py-4 align-top">
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => handleStartSession(session.id)}
                              className="rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                            >
                              {isSelectableGroup ? 'Start Group' : 'Start'}
                            </button>
                            <button
                              onClick={() => handleSendToMobile(session.id)}
                              disabled={sendingSmsSessionId === session.id}
                              className="rounded-full border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 transition hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {sendingSmsSessionId === session.id ? 'Sending...' : 'Send'}
                            </button>
                            <button
                              className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                              onClick={() => handleRescheduleRequest(session.id)}
                            >
                              Reschedule
                            </button>
                            {session.sessionDate === todayKey && (
                              <button
                                className="rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100"
                                onClick={() => handleSetStatus(session.selectionId, session.sessionDate, 'COMPLETED')}
                              >
                                Mark completed
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
            <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
              <h3 className="text-xl font-semibold tracking-tight text-slate-950">Completed Sessions</h3>
              <p className="mt-1 text-sm text-slate-500">Update missed daily statuses and correct sessions that were marked incorrectly.</p>
            </div>
            <ul className="divide-y divide-slate-200">
              {completedSessions.length === 0 ? (
                <li className="px-6 py-6 text-sm text-slate-500">No completed sessions</li>
              ) : (
                completedSessions.map((session) => (
                  <li key={session.id} className="flex flex-col gap-4 px-5 py-5 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-sky-700">{resolveSubjectLabel(session.subject, session.subjectId)}</p>
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                          {session.status}
                        </span>
                      </div>
                      <p className="mt-2 text-sm font-medium text-slate-900">{session.studentName}</p>
                      <p className="mt-1 text-sm text-slate-500">{formatSessionTime(session.scheduledAt)}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {session.status === 'COMPLETED' ? (
                        <button
                          className="rounded-full border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800 transition hover:bg-amber-100"
                          onClick={() => handleSetStatus(session.selectionId, session.sessionDate, 'SCHEDULED')}
                        >
                          Mark pending
                        </button>
                      ) : (
                        <button
                          className="rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                          onClick={() => handleSetStatus(session.selectionId, session.sessionDate, 'COMPLETED')}
                        >
                          Mark completed
                        </button>
                      )}
                    </div>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      ) : activeTab === 'students' ? (
        <div className="space-y-6">
          <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
            <div className="border-b border-slate-200 bg-slate-950 px-5 py-5 text-white sm:px-6">
              <h3 className="text-xl font-semibold tracking-tight">Search Students</h3>
              <p className="mt-1 max-w-2xl text-sm text-slate-300">Find students by name, email, or grade and jump directly into their details.</p>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <StudentSearch initialSearch={studentSearchQuery} />
            </div>
          </div>

          <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
            <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
              <h3 className="text-xl font-semibold tracking-tight text-slate-950">Recent Students</h3>
              <p className="mt-1 max-w-2xl text-sm text-slate-500">Students you&apos;ve recently taught, with quick progress context and direct profile access.</p>
            </div>
            <div className="md:hidden divide-y divide-slate-200">
              {recentStudents.length === 0 ? (
                <div className="px-5 py-8 text-sm text-slate-500">No recent students</div>
              ) : (
                recentStudents.map((student) => (
                  <div key={student.id} className="space-y-4 px-5 py-5">
                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-950 text-xs font-semibold text-white">
                        {student.avatar}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-950">{student.name}</p>
                        <p className="text-xs text-sky-700">{resolveSubjectLabel(student.subject, student.subjectId)}</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">Last session: {student.lastSession}</p>
                    <div className="flex items-center gap-2">
                      <div className="h-2.5 flex-1 rounded-full bg-slate-200">
                        <div className="h-2.5 rounded-full bg-emerald-500" style={{ width: `${student.progress}%` }} />
                      </div>
                      <span className="text-xs text-slate-500">{student.progress}%</span>
                    </div>
                    <button
                      onClick={() => handleViewStudent(student.id)}
                      className="rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                    >
                      View student
                    </button>
                  </div>
                ))
              )}
            </div>
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Student
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Subject
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Last Session
                    </th>
                    <th scope="col" className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                      Progress
                    </th>
                    <th scope="col" className="relative px-6 py-4">
                      <span className="sr-only">View</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {recentStudents.map((student) => (
                    <tr key={student.id} className="transition hover:bg-slate-50/80">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-slate-950 font-medium text-white">
                            {student.avatar}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-semibold text-slate-950">{student.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-sky-700">{resolveSubjectLabel(student.subject, student.subjectId)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-slate-500">{student.lastSession}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="mr-2 h-2.5 w-32 rounded-full bg-slate-200">
                            <div className="h-2.5 rounded-full bg-emerald-500" style={{ width: `${student.progress}%` }}></div>
                          </div>
                          <span className="text-sm text-slate-500">{student.progress}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleViewStudent(student.id)}
                          className="rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : activeTab === 'analytics' && (
        <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
          <div className="border-b border-slate-200 bg-slate-950 px-5 py-5 text-white sm:px-6">
            <h3 className="text-xl font-semibold tracking-tight">Analytics</h3>
            <p className="mt-1 max-w-2xl text-sm text-slate-300">Your teaching performance and revenue insights will appear here.</p>
          </div>
          <div className="px-5 py-6 sm:px-6">
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Session consistency</p>
                <p className="mt-3 text-2xl font-semibold text-slate-950">{completedSessions.length}</p>
                <p className="mt-2 text-sm text-slate-500">Completed sessions already captured in your dashboard history.</p>
              </div>
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Learner activity</p>
                <p className="mt-3 text-2xl font-semibold text-slate-950">{recentStudents.length}</p>
                <p className="mt-2 text-sm text-slate-500">Recently active students contributing to your dashboard movement.</p>
              </div>
              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Next release</p>
                <p className="mt-3 text-2xl font-semibold text-slate-950">Insights</p>
                <p className="mt-2 text-sm text-slate-500">Trend charts, payout summaries, and student retention graphs are planned next.</p>
              </div>
            </div>
            <div className="mt-5 flex h-56 items-center justify-center rounded-[1.75rem] border border-dashed border-slate-300 bg-slate-50 text-center">
              <div>
                <AcademicCapIcon className="mx-auto h-10 w-10 text-slate-400" />
                <p className="mt-4 text-base font-semibold text-slate-900">Analytics dashboard coming soon</p>
                <p className="mt-2 text-sm text-slate-500">The visual reporting layer is not wired yet, but the rest of the workflow remains available.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1.25fr)_minmax(300px,0.95fr)]">
        <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
          <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
            <h3 className="text-xl font-semibold tracking-tight text-slate-950">Quick Actions</h3>
            <p className="mt-1 text-sm text-slate-500">Jump directly into the workflows teachers use most often.</p>
          </div>
          <div className="grid grid-cols-1 gap-4 px-5 py-5 sm:grid-cols-2 sm:px-6">
            <Link to="/teacher/dashboard?tab=sessions" className="group rounded-[1.5rem] bg-slate-950 p-5 text-white transition hover:bg-slate-900">
              <CalendarIcon className="h-6 w-6" aria-hidden="true" />
              <div className="mt-8">
                <p className="text-base font-semibold">Class Sessions</p>
                <p className="mt-2 text-sm text-slate-300">Start classes, share links or QR codes, and mark sessions complete.</p>
              </div>
            </Link>
            <Link to="/teacher/dashboard?tab=students" className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 transition hover:bg-slate-100">
              <UserGroupIcon className="h-6 w-6 text-slate-900" aria-hidden="true" />
              <div className="mt-8">
                <p className="text-base font-semibold text-slate-950">Manage Students</p>
                <p className="mt-2 text-sm text-slate-500">Search students, review recent learners, and revisit class context.</p>
              </div>
            </Link>
            <Link to="/teacher/exams" className="rounded-[1.5rem] border border-emerald-200 bg-emerald-50 p-5 transition hover:bg-emerald-100">
              <AcademicCapIcon className="h-6 w-6 text-emerald-700" aria-hidden="true" />
              <div className="mt-8">
                <p className="text-base font-semibold text-emerald-900">Manage Exams</p>
                <p className="mt-2 text-sm text-emerald-700/80">Build and publish tests for your students from one place.</p>
              </div>
            </Link>
            <button
              type="button"
              onClick={handleOpenProfile}
              className="rounded-[1.5rem] border border-sky-200 bg-sky-50 p-5 text-left transition hover:bg-sky-100"
            >
              <ClockIcon className="h-6 w-6 text-sky-700" aria-hidden="true" />
              <div className="mt-8">
                <p className="text-base font-semibold text-sky-950">Set Availability</p>
                <p className="mt-2 text-sm text-sky-700/80">Refine profile readiness, time slots, and visibility settings.</p>
              </div>
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-[1.75rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.4)]">
          <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
            <h3 className="text-xl font-semibold tracking-tight text-slate-950">Teacher Workflow</h3>
            <p className="mt-1 text-sm text-slate-500">A clear order for setup, leads, classes, exams, and earnings.</p>
          </div>
          <div className="grid grid-cols-1 gap-3 px-5 py-5 sm:px-6">
            <Link to="/teacher/dashboard?tab=profile" className="rounded-[1.25rem] border border-slate-200 p-4 transition hover:bg-slate-50">
              <p className="text-sm font-semibold text-slate-950">Create Profile</p>
              <p className="mt-1 text-xs text-slate-500">Profile and teaching certificate setup</p>
            </Link>
            <Link to="/teacher/leads" className="rounded-[1.25rem] border border-slate-200 p-4 transition hover:bg-slate-50">
              <p className="text-sm font-semibold text-slate-950">Get Leads</p>
              <p className="mt-1 text-xs text-slate-500">Students who selected you for classes</p>
            </Link>
            <Link to="/teacher/classes" className="rounded-[1.25rem] border border-slate-200 p-4 transition hover:bg-slate-50">
              <p className="text-sm font-semibold text-slate-950">Conduct Classes</p>
              <p className="mt-1 text-xs text-slate-500">Upcoming sessions and completion flow</p>
            </Link>
            <Link to="/teacher/exams" className="rounded-[1.25rem] border border-slate-200 p-4 transition hover:bg-slate-50">
              <p className="text-sm font-semibold text-slate-950">Create Exams</p>
              <p className="mt-1 text-xs text-slate-500">Create, publish, and manage exams</p>
            </Link>
            <Link to="/teacher/earnings" className="rounded-[1.25rem] border border-slate-200 p-4 transition hover:bg-slate-50">
              <p className="text-sm font-semibold text-slate-950">Earn From Enrollments</p>
              <p className="mt-1 text-xs text-slate-500">Track wallet and payment history</p>
            </Link>
            <Link to="/teacher/subscription-plans#subscription-guarantee" className="rounded-[1.25rem] border border-slate-200 p-4 transition hover:bg-slate-50">
              <p className="text-sm font-semibold text-slate-950">Subscription Guarantee</p>
              <p className="mt-1 text-xs text-slate-500">Clear subscription assurance and plan continuity</p>
            </Link>
          </div>
        </div>
      </div>

      {rescheduleSessionId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md space-y-4 rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-[0_30px_90px_-45px_rgba(15,23,42,0.55)]">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-base font-semibold text-slate-950">Reschedule Session</p>
                <p className="mt-1 text-xs text-slate-500">Select a new date/time and share the reason for admin approval.</p>
              </div>
              <button
                onClick={closeRescheduleModal}
                className="text-sm font-medium text-slate-500 hover:text-slate-700"
              >
                Close
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Reason</label>
                <textarea
                  value={rescheduleReason}
                  onChange={(e) => setRescheduleReason(e.target.value)}
                  rows={3}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                  placeholder="Reason for rescheduling"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Proposed Date & Time</label>
                <input
                  type="datetime-local"
                  value={rescheduleDateTime}
                  onChange={(e) => setRescheduleDateTime(e.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-400 focus:bg-white"
                />
              </div>
              {rescheduleError && <p className="text-xs text-red-700">{rescheduleError}</p>}
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={closeRescheduleModal}
                className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={submitRescheduleRequest}
                className="rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Submit Request
              </button>
            </div>
          </div>
        </div>
      )}

      {groupStartSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-lg space-y-4 rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-[0_30px_90px_-45px_rgba(15,23,42,0.55)]">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-base font-semibold text-slate-950">Start Group Session</p>
                <p className="mt-1 text-xs text-slate-500">
                  Choose the students joining this shared slot before you launch it.
                </p>
              </div>
              <button
                onClick={closeGroupStartModal}
                className="text-sm font-medium text-slate-500 hover:text-slate-700"
              >
                Close
              </button>
            </div>
            <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 px-4 py-3">
              <p className="text-sm font-semibold text-slate-950">
                {resolveSubjectLabel(groupStartSession.subject, groupStartSession.subjectId)}
              </p>
              <p className="mt-1 text-xs text-slate-500">{formatSessionTime(groupStartSession.scheduledAt)}</p>
            </div>
            <div className="space-y-2">
              {groupStartOptions.map((session) => {
                const checked = selectedGroupSessionIds.includes(session.id);

                return (
                  <label
                    key={session.id}
                    className={`flex cursor-pointer items-start gap-3 rounded-[1.25rem] border px-4 py-3 transition ${
                      checked ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleGroupStartSession(session.id)}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-950">{session.studentName}</p>
                      <p className="mt-1 text-xs text-slate-500">
                        {session.sessionDate === todayKey ? 'Scheduled for today' : `Session date ${session.sessionDate}`}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="flex items-center justify-between rounded-[1.25rem] bg-slate-50 px-4 py-3 text-xs text-slate-600">
              <span>Selected {selectedGroupSessionIds.length}</span>
              <span>{formatStudentCount(groupStartOptions.length)} available</span>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              <button
                onClick={() => setSelectedGroupSessionIds(groupStartOptions.map((session) => session.id))}
                className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Select All
              </button>
              <button
                onClick={() => setSelectedGroupSessionIds([])}
                className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Clear
              </button>
              <button
                onClick={startSelectedGroupSessions}
                disabled={!selectedGroupSessionIds.length}
                className="rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Start Session
              </button>
            </div>
          </div>
        </div>
      )}

      {qrPrimarySession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md space-y-4 rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-[0_30px_90px_-45px_rgba(15,23,42,0.55)]">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-950">Offline Session QR</p>
                <p className="mt-1 text-xs text-slate-500">
                  {summarizeStudents(qrSessions)} | {resolveSubjectLabel(qrPrimarySession.subject, qrPrimarySession.subjectId)}
                </p>
              </div>
              <button
                onClick={() => setQrSessions([])}
                className="text-sm font-medium text-slate-500 hover:text-slate-700"
              >
                Close
              </button>
            </div>
            <div className="flex justify-center rounded-[1.5rem] bg-slate-50 p-4">
              <img src={qrUrl} alt="Session QR" className="h-64 w-64 rounded-2xl" />
            </div>
            <div className="break-all rounded-2xl bg-slate-50 px-4 py-3 text-center text-xs text-slate-700">
              QR/Text: {qrText}
            </div>
            {qrSessions.length > 1 && (
              <div className="rounded-2xl bg-slate-50 px-4 py-3 text-xs text-slate-600">
                Students: {qrSessions.map((session) => session.studentName).join(', ')}
              </div>
            )}
            <p className="text-center text-xs text-slate-500">
              Scan to confirm attendance for this offline session.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherDashboard;
